import React from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {ButtonPrimary, TwitterHollowIcon, LinkedInHollowIcon, FacebookHollowIcon} from '@modifi/ui'
import {white} from '@modifi/colors'
import {KEY_JANUS, extractFromLocale, useTranslation} from '@modifi/plugin-i18n'

import {CnBrandLogo} from '../../../assets/graphics/cn-brand-logo'
import WeChatQRCode from '../../../assets/images/we-chat-qr-code.png'
import {
  StyledFooterSection,
  StyledSocialLink,
  SocialLinks,
  CTAWrapper,
  UpdatedBrandLogoHollow,
  QRCodeWrapper,
} from './styles'
import {TRACK_ACTION_OUTBOUND_LINK} from '../../../constants/tracking'
import Link from '../../../features/common/link'
import {useCurrentRegion} from '../../../features/route-info'

export const isCnTldOrRegion = (region?: string) => {
  if (typeof window === 'undefined') {
    return null
  }
  return region === 'cn' || window?.location?.hostname?.endsWith('.cn')
}

const socialLinkPropTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

const socialLinkDefaultProps = {}

export type SocialLinkProps = InferPropTypes<
  typeof socialLinkPropTypes,
  typeof socialLinkDefaultProps
>

export const SocialLink: React.FC<SocialLinkProps> = ({to, children, ...restProps}) => (
  <StyledSocialLink href={to} target="_blank" rel="noopener noreferrer" {...restProps}>
    {children}
  </StyledSocialLink>
)

SocialLink.propTypes = socialLinkPropTypes

const footerSectionPropTypes = {
  t: PropTypes.func.isRequired,
  first: PropTypes.bool,
  second: PropTypes.bool,
  extraLinks: PropTypes.shape({
    linkedIn: PropTypes.string,
    signup: PropTypes.string,
    twitter: PropTypes.string,
    facebook: PropTypes.string,
  }).isRequired,
}

const footerSectionDefaultProps = {
  first: false,
  second: false,
}

export type FooterSectionProps = InferPropTypes<
  typeof footerSectionPropTypes,
  typeof footerSectionDefaultProps
>

export const FooterSection: React.FC<FooterSectionProps> = ({t, extraLinks, ...props}) => {
  const {i18n} = useTranslation(KEY_JANUS)
  const [, region] = extractFromLocale(i18n.language)
  return (
    <StyledFooterSection {...props}>
      {isCnTldOrRegion(region) !== true ? (
        <SocialLinks>
          <SocialLink
            to={extraLinks.linkedIn || ''}
            aria-label="Modifi LinkedIn Profile"
            data-track-action={TRACK_ACTION_OUTBOUND_LINK}
            data-track-label="LinkedIn Logo"
          >
            <LinkedInHollowIcon boxColor={white} />
          </SocialLink>
          <SocialLink
            to={extraLinks.twitter || ''}
            aria-label="Modifi Twitter Profile"
            data-track-action={TRACK_ACTION_OUTBOUND_LINK}
            data-track-label="Twitter Logo"
          >
            <TwitterHollowIcon boxColor={white} />
          </SocialLink>
          <SocialLink
            to={extraLinks.facebook || ''}
            aria-label="Modifi Faecebook Profile"
            data-track-action={TRACK_ACTION_OUTBOUND_LINK}
            data-track-label="Facebook Logo"
          >
            <FacebookHollowIcon boxColor={white} />
          </SocialLink>
        </SocialLinks>
      ) : (
        <QRCodeWrapper>
          {/* need to later change to webp formats */}
          <img src={WeChatQRCode} alt="wechat-qr-code" height={80} width={80} />
        </QRCodeWrapper>
      )}
      <CTAWrapper>
        <Link type="button" buttonComponent={ButtonPrimary} to={extraLinks.signup || ''}>
          {t('footer.cta.sign-up')}
        </Link>
      </CTAWrapper>
    </StyledFooterSection>
  )
}

FooterSection.propTypes = footerSectionPropTypes
FooterSection.defaultProps = footerSectionDefaultProps

// eslint-disable-next-line react/prop-types
export const CountryAwareBrandLogo = () => {
  const region = useCurrentRegion()
  switch (region) {
    case 'cn':
      return <CnBrandLogo height={undefined} width={undefined} />
    default:
      return <UpdatedBrandLogoHollow boxColor={white} />
  }
}
